/**
 * Page de redirection vers le paiement (achat direct depuis le marketing).
 *
 * Accessible sans compte. URL : /acheter?kind=pass&type=…&duration=… ou
 * /acheter?kind=subscription&plan=…
 *
 * Invoque create-public-checkout (sans auth, Stripe collecte l'email) puis
 * redirige vers Stripe. Le compte est créé au paiement (webhook) ; le retour
 * Stripe pointe vers /bienvenue.
 */

import { useEffect, useState } from 'react';
import { supabaseUrl, supabaseAnonKey } from '@/lib/supabase';
import { markCheckoutStarted, passCheckoutMark } from '@/lib/analytics/track';

type State =
  | { status: 'redirecting' }
  | { status: 'unavailable' }
  | { status: 'error'; message: string };

function buildBody(): Record<string, unknown> | null {
  const p = new URLSearchParams(window.location.search);
  const kind = p.get('kind');
  // Le checkout marketing renvoie ici ; on transmet l'origine en cancel_url.
  const returnTo =
    document.referrer && !document.referrer.includes(window.location.host)
      ? document.referrer
      : undefined;

  if (kind === 'pass') {
    return {
      kind: 'pass',
      type: p.get('type'),
      duration: p.get('duration'),
      ...(p.get('promo') ? { promo: p.get('promo') } : {}),
      ...(returnTo ? { return_to: returnTo } : {}),
    };
  }
  if (kind === 'subscription') {
    return {
      kind: 'subscription',
      plan: p.get('plan'),
      ...(returnTo ? { return_to: returnTo } : {}),
    };
  }
  return null;
}

const MARKETING_URL =
  (import.meta.env.VITE_MARKETING_URL as string | undefined) ?? 'https://jeraisonne.fr';

export function AcheterPage() {
  const [state, setState] = useState<State>({ status: 'redirecting' });

  useEffect(() => {
    const body = buildBody();
    if (!body) {
      setState({ status: 'error', message: 'Lien d’achat invalide.' });
      return;
    }

    markCheckoutStarted(
      body.kind === 'subscription'
        ? body.plan === 'family_monthly'
          ? 'abonnement-famille'
          : 'abonnement-eleve'
        : passCheckoutMark(
            body.type === 'epreuves' ? 'epreuves' : 'normal',
            body.duration === '24h' || body.duration === '7d' ? body.duration : '30d',
          ),
    );
    // fetch direct (et non supabase.functions.invoke) pour lire le corps même
    // sur un statut non-2xx (ex. 502 price_not_provisioned).
    (async () => {
      try {
        const res = await fetch(`${supabaseUrl}/functions/v1/create-public-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            apikey: supabaseAnonKey,
            Authorization: `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify(body),
        });
        const data = (await res.json()) as { url?: string; error?: string };
        if (res.ok && data.url) {
          window.location.href = data.url;
          return;
        }
        if (data.error === 'price_not_provisioned') {
          setState({ status: 'unavailable' });
          return;
        }
        if (data.error === 'pass_disabled') {
          setState({
            status: 'error',
            message:
              'Cette formule n’est plus proposée. Choisis l’abonnement 30 jours sur le site.',
          });
          return;
        }
        setState({ status: 'error', message: 'Impossible de démarrer le paiement.' });
      } catch {
        setState({ status: 'error', message: 'Impossible de démarrer le paiement.' });
      }
    })();
  }, []);

  return (
    <div className="bg-ink-50 flex min-h-dvh items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm space-y-6 text-center">
        <p className="font-display text-primary-600 text-2xl font-extrabold tracking-tight">
          Jeraisonne
        </p>

        {state.status === 'redirecting' && (
          <div className="space-y-4">
            <div className="border-primary-400 mx-auto h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
            <p className="text-ink-600 font-sans text-sm">Redirection vers le paiement sécurisé…</p>
          </div>
        )}

        {state.status === 'unavailable' && (
          <div className="space-y-3 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
            <p className="text-2xl">⏳</p>
            <p className="text-ink-700 font-sans font-semibold">Offre bientôt disponible</p>
            <p className="text-ink-500 font-sans text-sm">
              Cette formule arrive très vite. En attendant, tu peux choisir un pass.
            </p>
            <a
              href={`${MARKETING_URL}/`}
              className="bg-primary-500 hover:bg-primary-600 inline-block min-h-11 rounded-xl px-5 py-2.5 text-sm font-semibold text-white"
            >
              Voir les offres
            </a>
          </div>
        )}

        {state.status === 'error' && (
          <div className="space-y-3 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
            <p className="text-2xl">⚠️</p>
            <p className="text-ink-700 font-sans font-semibold">{state.message}</p>
            <a
              href={`${MARKETING_URL}/`}
              className="text-primary-600 inline-block text-sm font-semibold hover:underline"
            >
              Retour aux offres
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
